import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { BillingListQuery } from '../../domain/models/billing-list-query.model';
import { ClientSearchResult } from '../../domain/models/client-search-result.model';
import { Estimate } from '../../domain/models/estimate.model';
import { EstimateItem } from '../../domain/models/estimate-item.model';
import { EstimateListQuery } from '../../domain/models/estimate-list-query.model';
import { Invoice } from '../../domain/models/invoice.model';
import { InvoiceRow } from '../../domain/models/invoice-row.model';
import { Payment } from '../../domain/models/payment.model';
import { PatientSearchResult } from '../../domain/models/patient-search-result.model';
import {
  BillingListResult, EstimateListResult, IBillingRepository,
} from '../../domain/repositories/billing.repository';
import { BillingRemoteDatasource } from '../datasources/remote/billing.remote-datasource';
import { ClientSearchResultMapper } from '../mappers/client-search-result.mapper';
import { EstimateMapper } from '../mappers/estimate.mapper';
import { EstimateItemMapper } from '../mappers/estimate-item.mapper';
import { InvoiceMapper } from '../mappers/invoice.mapper';
import { InvoiceRowMapper } from '../mappers/invoice-row.mapper';
import { PatientSearchResultMapper } from '../mappers/patient-search-result.mapper';
import { PaymentMapper } from '../mappers/payment.mapper';

@Injectable()
export class BillingRepositoryImpl implements IBillingRepository {
  private readonly datasource = inject(BillingRemoteDatasource);
  private readonly invoiceMapper = inject(InvoiceMapper);
  private readonly invoiceRowMapper = inject(InvoiceRowMapper);
  private readonly paymentMapper = inject(PaymentMapper);
  private readonly estimateMapper = inject(EstimateMapper);
  private readonly estimateItemMapper = inject(EstimateItemMapper);
  private readonly clientSearchResultMapper = inject(ClientSearchResultMapper);
  private readonly patientSearchResultMapper = inject(PatientSearchResultMapper);

  public list(query: BillingListQuery): Observable<BillingListResult> {
    return this.datasource.listInvoices({
      page: query.page,
      pageSize: query.pageSize,
      departmentId: query.departmentId,
      onlyPending: query.onlyPending,
      paymentState: query.paymentState,
      dateFrom: query.dateFrom,
      dateTo: query.dateTo,
      clientIds: query.clientIds,
      invoiceDueDateFrom: query.invoiceDueDateFrom,
      invoiceDueDateTo: query.invoiceDueDateTo,
      datePaidFrom: query.datePaidFrom,
      datePaidTo: query.datePaidTo,
      dueSumMin: query.dueSumMin,
      dueSumMax: query.dueSumMax,
      invoiceNumber: query.invoiceNumber,
      invoicePrefixContains: query.invoicePrefixContains,
      sortBy: query.sortBy,
      sortDir: query.sortDir,
    }).pipe(
      map((dto) => ({
        items: dto.Items.map((item) => this.invoiceMapper.toInvoice(item)),
        totalCount: dto.TotalCount,
        asOfUtc: dto.AsOfUtc ?? null,
      })),
    );
  }

  public getInvoiceById(invoiceId: string): Observable<Invoice> {
    return this.datasource.getInvoiceById(invoiceId).pipe(map((dto) => this.invoiceMapper.toInvoice(dto)));
  }

  public getInvoiceRows(invoiceId: string): Observable<readonly InvoiceRow[]> {
    return this.datasource.getInvoiceRows(invoiceId).pipe(
      map((dto) => dto.Items.map((item) => this.invoiceRowMapper.toInvoiceRow(item))),
    );
  }

  public getInvoicePayments(invoiceId: string): Observable<readonly Payment[]> {
    return this.datasource.getInvoicePayments(invoiceId).pipe(
      map((dto) => dto.Items.map((item) => this.paymentMapper.toPayment(item))),
    );
  }

  public getInvoicePdf(invoiceId: string): Observable<Blob> {
    return this.datasource.getInvoicePdf(invoiceId);
  }

  public listEstimates(query: EstimateListQuery): Observable<EstimateListResult> {
    return this.datasource.listEstimates({
      page: query.page,
      pageSize: query.pageSize,
      clientIds: query.clientIds,
      patientIds: query.patientIds,
      statuses: query.statuses,
      archived: query.archived,
      dateFrom: query.dateFrom,
      dateTo: query.dateTo,
      sortBy: query.sortBy,
      sortDir: query.sortDir,
    }).pipe(
      map((dto) => ({
        items: dto.Items.map((item) => this.estimateMapper.toEstimate(item)),
        totalCount: dto.TotalCount,
        asOfUtc: dto.AsOfUtc ?? null,
      })),
    );
  }

  public getEstimateById(estimateId: string): Observable<Estimate> {
    return this.datasource.getEstimateById(estimateId).pipe(map((dto) => this.estimateMapper.toEstimate(dto)));
  }

  public getEstimateItems(estimateId: string): Observable<readonly EstimateItem[]> {
    return this.datasource.getEstimateItems(estimateId).pipe(
      map((items) => items.map((item) => this.estimateItemMapper.toEstimateItem(item))),
    );
  }

  public getEstimatePdf(estimateId: string): Observable<Blob> {
    return this.datasource.getEstimatePdf(estimateId);
  }

  public searchClients(term: string): Observable<readonly ClientSearchResult[]> {
    return this.datasource.searchClients(term).pipe(
      map((dto) => dto.Items.map((item) => this.clientSearchResultMapper.toClientSearchResult(item))),
    );
  }

  public searchPatients(term: string): Observable<readonly PatientSearchResult[]> {
    return this.datasource.searchPatients(term).pipe(
      map((dto) => dto.Items.map((item) => this.patientSearchResultMapper.toPatientSearchResult(item))),
    );
  }
}
